// client/src/pages/application/ResultsPage.js
// Results & Analytics — 30-day performance + AI strategy insights

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ─── helpers ────────────────────────────────────────────────────── */
const getToken = () => localStorage.getItem('token');
const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw body;
  return body;
};

const fmt = (n, type = 'num') => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (isNaN(v)) return '—';
  if (type === 'pct') return `${v.toFixed(1)}%`;
  if (type === 'k')   return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
  return v.toLocaleString();
};

// Time-ago helper
const timeAgo = (isoStr) => {
  if (!isoStr) return null;
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// Safely extract string from insight item (AI returns objects or strings)
const insightText = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.insight || item.action || item.recommendation || item.description || item.text || JSON.stringify(item);
};

const PLATFORM_CFG = {
  facebook:  { label: 'Facebook',  icon: 'fab fa-facebook-f',   dot: 'bg-blue-600',   text: 'text-blue-700',  bg: 'bg-blue-50',  border: 'border-blue-200',  auto: true  },
  instagram: { label: 'Instagram', icon: 'fab fa-instagram',    dot: 'bg-pink-500',   text: 'text-pink-700',  bg: 'bg-pink-50',  border: 'border-pink-200',  auto: true  },
  linkedin:  { label: 'LinkedIn',  icon: 'fab fa-linkedin-in',  dot: 'bg-blue-800',   text: 'text-blue-900',  bg: 'bg-blue-50',  border: 'border-blue-300',  auto: false },
  tiktok:    { label: 'TikTok',    icon: 'fab fa-tiktok',       dot: 'bg-gray-900',   text: 'text-gray-800',  bg: 'bg-gray-50',  border: 'border-gray-300',  auto: false },
};

const MANUAL_FIELDS = {
  linkedin: [
    { key: 'impressions', label: 'Impressions',    icon: 'fa-eye'           },
    { key: 'clicks',      label: 'Link Clicks',    icon: 'fa-mouse-pointer' },
    { key: 'engagement',  label: 'Engagements',    icon: 'fa-heart'         },
    { key: 'followers',   label: 'New Followers',  icon: 'fa-user-plus'     },
    { key: 'posts',       label: 'Posts Published',icon: 'fa-paper-plane'   },
  ],
  tiktok: [
    { key: 'views',       label: 'Video Views',    icon: 'fa-play'          },
    { key: 'likes',       label: 'Likes',          icon: 'fa-heart'         },
    { key: 'comments',    label: 'Comments',       icon: 'fa-comment'       },
    { key: 'shares',      label: 'Shares',         icon: 'fa-share'         },
    { key: 'followers',   label: 'New Followers',  icon: 'fa-user-plus'     },
    { key: 'posts',       label: 'Posts Published',icon: 'fa-paper-plane'   },
  ],
};

/* ─── sub-components ──────────────────────────────────────────────── */
function StatCard({ icon, iconBg = 'bg-slate-700', label, value, sub, trend, trendUp }) {
  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 relative overflow-hidden flex flex-col gap-2">
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(100,116,139,0.18) 1px,transparent 1px),linear-gradient(90deg,rgba(100,116,139,0.18) 1px,transparent 1px)',
        backgroundSize: '8px 8px',
        maskImage: 'radial-gradient(ellipse 80% 80% at 100% 100%, black 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 100% 100%, black 0%, transparent 70%)',
      }} />
      <div className="relative flex items-start justify-between">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
          <i className={`${icon} text-white text-xs`}></i>
        </div>
        {trend != null && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trendUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {trendUp ? '▲' : '▼'} {trend}
          </span>
        )}
      </div>
      <div className="relative">
        <div className="text-[24px] sm:text-[28px] font-extrabold text-slate-800 leading-none">{value}</div>
        <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function SectionHeader({ icon, title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon && <i className={`${icon} text-slate-400 text-sm`}></i>}
        <h2 className="text-[12px] sm:text-[13px] font-bold text-slate-800 uppercase tracking-wider">{title}</h2>
      </div>
      {action}
    </div>
  );
}

function InsightItem({ type, text }) {
  const cfg = {
    good:   { icon: 'fa-check-circle', colour: 'text-green-600', bg: 'bg-green-50'  },
    bad:    { icon: 'fa-times-circle',  colour: 'text-red-600',   bg: 'bg-red-50'    },
    tip:    { icon: 'fa-lightbulb',     colour: 'text-amber-600', bg: 'bg-amber-50'  },
    update: { icon: 'fa-arrow-circle-up', colour: 'text-blue-600', bg: 'bg-blue-50'  },
  };
  const c = cfg[type] || cfg.tip;
  const safeText = insightText(text);
  return (
    <div className={`flex items-start gap-2 p-2.5 rounded-lg ${c.bg} mb-2`}>
      <i className={`fas ${c.icon} ${c.colour} text-sm mt-0.5 shrink-0`}></i>
      <p className="text-[12px] text-slate-700 leading-relaxed">{safeText}</p>
    </div>
  );
}

function PublishedPostCard({ post, onRemove }) {
  const cfg = PLATFORM_CFG[post.platform] || PLATFORM_CFG.facebook;
  const eng = post.engagement || {};
  const likes      = eng.likes       ?? eng.post_reactions_like_total ?? null;
  const comments   = eng.comments    ?? null;
  const reach      = eng.reach       ?? eng.post_reach                ?? null;
  const impressions= eng.impressions  ?? eng.post_impressions          ?? null;
  const saves      = eng.saved       ?? null;
  const engaged    = eng.post_engaged_users ?? null;
  const hasMetrics = [likes, comments, reach, impressions].some(v => v != null);

  const metrics = [
    { v: likes,       label: 'Likes',       bg: 'bg-rose-50',   border: 'border-rose-100',   text: 'text-rose-600',   sub: 'text-rose-400'   },
    { v: comments,    label: 'Comments',    bg: 'bg-blue-50',   border: 'border-blue-100',   text: 'text-blue-600',   sub: 'text-blue-400'   },
    { v: reach,       label: 'Reach',       bg: 'bg-green-50',  border: 'border-green-100',  text: 'text-green-600',  sub: 'text-green-400'  },
    { v: impressions, label: 'Impressions', bg: 'bg-violet-50', border: 'border-violet-100', text: 'text-violet-600', sub: 'text-violet-400' },
    { v: saves,       label: 'Saves',       bg: 'bg-amber-50',  border: 'border-amber-100',  text: 'text-amber-600',  sub: 'text-amber-400'  },
    { v: engaged,     label: 'Engaged',     bg: 'bg-slate-50',  border: 'border-slate-100',  text: 'text-slate-600',  sub: 'text-slate-400'  },
  ].filter(m => m.v != null);

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Thumbnail or platform banner */}
      {post.imageUrl && !post.imageUrl.startsWith('data:') ? (
        <div className="w-full h-32 bg-gray-100 overflow-hidden">
          <img src={post.imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`w-full h-9 ${cfg.bg} flex items-center justify-center`}>
          <i className={`${cfg.icon} ${cfg.text} text-xl`}></i>
        </div>
      )}

      <div className="p-3 flex flex-col flex-1 gap-2.5">
        {/* Platform + Day badge + Remove */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className={`w-5 h-5 rounded ${cfg.bg} ${cfg.border} border flex items-center justify-center`}>
              <i className={`${cfg.icon} ${cfg.text} text-[9px]`}></i>
            </div>
            <span className="text-[11px] font-semibold text-slate-600">{cfg.label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {post.day && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">Day {post.day}</span>
            )}
            {onRemove && (
              <button
                onClick={onRemove}
                title="Remove from tracking (post was deleted)"
                className="w-5 h-5 rounded flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <i className="fas fa-times text-[10px]"></i>
              </button>
            )}
          </div>
        </div>

        {/* Caption */}
        <p className="text-[12px] text-slate-700 line-clamp-2 leading-relaxed flex-1">
          {post.caption || '(No caption)'}
        </p>

        {/* Engagement grid */}
        {hasMetrics ? (
          <div className={`grid gap-1.5 ${metrics.length >= 4 ? 'grid-cols-2' : 'grid-cols-2'}`}>
            {metrics.map(m => (
              <div key={m.label} className={`${m.bg} ${m.border} border rounded-lg p-2 text-center`}>
                <div className={`text-[15px] font-extrabold ${m.text}`}>{fmt(m.v, 'k')}</div>
                <div className={`text-[9px] ${m.sub} uppercase tracking-wide font-medium`}>{m.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 text-center">
            <p className="text-[11px] text-slate-400">
              {post.lastEngagementSync ? 'No metrics available yet — check back in 24h' : 'Tap Refresh to load metrics'}
            </p>
          </div>
        )}

        {/* Footer: published date + last synced */}
        <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-gray-100">
          <span>
            {post.publishedAt
              ? <><i className="fas fa-paper-plane mr-1"></i>{new Date(post.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}</>
              : <span className="italic">Date unknown</span>}
          </span>
          <span className="flex items-center gap-1">
            <i className="fas fa-sync-alt"></i>
            {post.lastEngagementSync ? timeAgo(post.lastEngagementSync) : <span className="text-amber-500">Not synced</span>}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── main page ───────────────────────────────────────────────────── */
export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // OAuth callback
  const [oauthMsg, setOauthMsg] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const error   = params.get('error');
    if (success === 'facebook_connected') setOauthMsg({ type: 'success', text: 'Facebook & Instagram connected successfully!' });
    if (error) setOauthMsg({ type: 'error', text: decodeURIComponent(error).replace(/_/g, ' ') });
    if (success || error) navigate('/results', { replace: true });
  }, [location.search, navigate]);

  // State
  const [loading, setLoading]       = useState(true);
  const [syncing, setSyncing]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying]     = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);

  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [roadmapData, setRoadmapData]   = useState(null);
  const [postAnalytics, setPostAnalytics] = useState([]);
  const [platformInsights, setPlatformInsights] = useState({});
  const [aiInsights, setAiInsights]     = useState(null);
  const [manualData, setManualData]     = useState({});

  const [manualModal, setManualModal]   = useState(null);
  const [manualForm, setManualForm]     = useState({});
  const [applyModal, setApplyModal]     = useState(false);
  const [activeTab, setActiveTab]       = useState('overview');
  const [syncMeta, setSyncMeta]         = useState(null); // { synced, cached, at }

  /* ── load all data ── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [accts, results, roadmap] = await Promise.allSettled([
        api('/api/social/accounts'),
        api('/api/results'),
        api('/api/roadmap'),
      ]);

      const accounts = accts.status === 'fulfilled' ? (accts.value.accounts || []) : [];
      setConnectedAccounts(accounts);

      if (results.status === 'fulfilled') {
        const rd = results.value;
        if (rd.summary?.manualPlatformData) setManualData(rd.summary.manualPlatformData);
        if (rd.summary?.aiInsights) setAiInsights(rd.summary.aiInsights);
        // Build platform insights from stored weekly metrics
        if (rd.weeklyMetrics?.length) {
          const pi = {};
          rd.weeklyMetrics.forEach(wm => {
            Object.keys(wm.platforms || {}).forEach(p => {
              if (!pi[p]) pi[p] = { impressions: 0, reach: 0, engagement: 0, followers: 0, posts: 0 };
              const pm = wm.platforms[p] || {};
              pi[p].impressions += pm.impressions || 0;
              pi[p].reach       += pm.reach       || 0;
              pi[p].engagement  += pm.engagement  || 0;
              pi[p].followers   += pm.followers   || 0;
              pi[p].posts       += pm.posts       || 0;
            });
          });
          setPlatformInsights(pi);
        }
      }

      if (roadmap.status === 'fulfilled') setRoadmapData(roadmap.value);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── sync post engagement from FB/IG (lazy cache) ── */
  const syncAnalytics = useCallback(async (forceRefresh = false) => {
    const hasFB = connectedAccounts.some(a => a.platform === 'facebook');
    const hasIG = connectedAccounts.some(a => a.platform === 'instagram');
    if (!hasFB && !hasIG) return;

    setSyncing(true);
    try {
      // POST /sync-metrics: reads calendar JSONB, fetches only stale posts from Meta API,
      // writes engagement back to DB, returns all published posts.
      // force=true bypasses the cache — used when user clicks Refresh manually.
      const data = await api('/api/social/sync-metrics', {
        method: 'POST',
        body: JSON.stringify({ force: forceRefresh })
      });
      const posts = data.posts || [];
      setPostAnalytics(posts);
      setSyncMeta({ synced: data.synced || 0, cached: data.cached || 0, at: new Date() });

      // Aggregate totals for the platform overview cards
      const pi = {};
      posts.forEach(p => {
        const plt = p.platform;
        if (!pi[plt]) pi[plt] = { impressions: 0, reach: 0, engagement: 0, followers: 0, posts: 0 };
        const eng = p.engagement || {};
        pi[plt].impressions += eng.impressions || eng.post_impressions || 0;
        pi[plt].reach       += eng.reach       || eng.post_reach       || 0;
        pi[plt].engagement  += eng.likes       || eng.post_reactions_like_total || 0;
        pi[plt].posts       += 1;
      });
      setPlatformInsights(prev => ({ ...prev, ...pi }));
    } catch {
      // non-fatal — cached data still shown
    } finally {
      setSyncing(false);
    }
  }, [connectedAccounts]);

  // Remove a post card (user deleted it from the platform)
  const removePost = useCallback(async (dayNumber) => {
    // Optimistically remove from UI first
    setPostAnalytics(prev => prev.filter(p => p.day !== dayNumber));
    try {
      await api(`/api/social/post-tracking/${dayNumber}`, { method: 'DELETE' });
    } catch {
      // If server fails, re-sync to restore correct state
      syncAnalytics();
    }
  }, [syncAnalytics]);

  // Auto-sync after accounts load
  const didAutoSync = React.useRef(false);
  useEffect(() => {
    if (!loading && !didAutoSync.current && connectedAccounts.length > 0) {
      didAutoSync.current = true;
      syncAnalytics();
    }
  }, [loading, connectedAccounts, syncAnalytics]);

  /* ── connect Meta (Facebook + Instagram) ── */
  const connectMeta = async () => {
    try {
      const data = await api('/api/social/connect/meta');
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      alert('Failed to start Facebook connection. Please try again.');
      console.error('Connect Meta error:', err);
    }
  };

  /* ── disconnect platform ── */
  const disconnectPlatform = async (platform) => {
    if (!window.confirm(`Disconnect ${PLATFORM_CFG[platform]?.label}? You can reconnect anytime.`)) return;
    setDisconnecting(platform);
    try {
      await api(`/api/social/disconnect/${platform}`, { method: 'DELETE' });
      setConnectedAccounts(prev => prev.filter(a => a.platform !== platform && a.platform !== (platform === 'facebook' ? 'instagram' : null)));
      // Also remove instagram if disconnecting facebook (Meta)
      if (platform === 'facebook') {
        setConnectedAccounts(prev => prev.filter(a => a.platform !== 'facebook' && a.platform !== 'instagram'));
      }
    } catch {
      alert('Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(null);
    }
  };

  /* ── generate AI insights ── */
  const generateInsights = async () => {
    setGenerating(true);
    try {
      // Build platformMetrics in the format the controller expects
      const pi = platformInsights;
      const platformMetrics = {
        instagram: pi.instagram ? {
          impressions:   pi.instagram.impressions,
          reach:         pi.instagram.reach,
          profile_views: pi.instagram.profile_views || 0,
          follower_count: pi.instagram.followers || 0,
        } : null,
        facebook: pi.facebook ? {
          page_impressions:    pi.facebook.impressions,
          page_reach:          pi.facebook.reach,
          page_fans:           pi.facebook.followers || 0,
          page_engaged_users:  pi.facebook.engagement || 0,
        } : null,
      };

      const result = await api('/api/results/ai-insights', {
        method: 'POST',
        body: JSON.stringify({ platformMetrics, manualData }),
      });

      const insights = result.insights || result;
      setAiInsights(insights);
    } catch (e) {
      const msg = e?.error || 'Failed to generate insights. Please try again.';
      alert(msg);
    } finally {
      setGenerating(false);
    }
  };

  /* ── save manual data ── */
  const saveManual = async () => {
    const platform = manualModal;
    try {
      await api('/api/results/save-manual', {
        method: 'POST',
        body: JSON.stringify({ platform, data: manualForm }),
      });
      setManualData(prev => ({ ...prev, [platform]: { ...manualForm } }));
      setManualModal(null);
      setManualForm({});
    } catch {
      alert('Failed to save data. Please try again.');
    }
  };

  /* ── apply strategy ── */
  const applyStrategy = async () => {
    setApplying(true);
    try {
      await api('/api/results/apply-strategy', {
        method: 'POST',
        body: JSON.stringify({ strategyUpdates: aiInsights?.strategyUpdates || {} }),
      });
      setApplyModal(false);
      setAiInsights(prev => prev ? { ...prev, applied: true } : prev);
    } catch {
      alert('Failed to apply strategy. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  /* ── computed ── */
  const hasFacebook  = connectedAccounts.some(a => a.platform === 'facebook');
  const hasInstagram = connectedAccounts.some(a => a.platform === 'instagram');

  const totalReach     = Object.values(platformInsights).reduce((s, p) => s + (p.reach || p.impressions || 0), 0)
                       + (manualData.linkedin?.impressions || 0) + (manualData.tiktok?.views || 0);
  const totalFollowers = Object.values(platformInsights).reduce((s, p) => s + (p.followers || 0), 0)
                       + (manualData.linkedin?.followers || 0) + (manualData.tiktok?.followers || 0);
  const totalPosts     = Object.values(platformInsights).reduce((s, p) => s + (p.posts || 0), 0)
                       + (manualData.linkedin?.posts || 0) + (manualData.tiktok?.posts || 0);
  const engRates       = postAnalytics.filter(p => p.engagement?.engagementRate != null).map(p => p.engagement.engagementRate);
  const avgEngRate     = engRates.length ? engRates.reduce((s, v) => s + v, 0) / engRates.length : 0;

  const calendarDays   = roadmapData?.calendar?.days || [];
  const completedCount = calendarDays.filter(d => d.status === 'completed').length;
  const scheduledCount = calendarDays.filter(d => d.hasPost).length;
  const completionPct  = scheduledCount > 0 ? Math.round((completedCount / scheduledCount) * 100) : 0;

  const keyChanges = aiInsights?.strategyUpdates?.keyChanges || [];

  /* ── loading ── */
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-slate-500">Loading analytics…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* ════════════════════════════════════════════════════════════════ */
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="p-5 space-y-4 flex-1">

        {/* OAuth message */}
        {oauthMsg && (
          <div className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${oauthMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <i className={`fas ${oauthMsg.type === 'success' ? 'fa-check-circle text-green-600' : 'fa-exclamation-circle text-red-600'}`}></i>
            <span className="flex-1 text-sm">{oauthMsg.text}</span>
            <button onClick={() => setOauthMsg(null)} className="text-slate-400 hover:text-slate-600"><i className="fas fa-times"></i></button>
          </div>
        )}

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-extrabold text-slate-800">Results & Analytics</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">30-day campaign performance · AI-powered strategy insights</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {syncing && (
              <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
                <div className="w-3 h-3 border border-blue-400 border-t-blue-600 rounded-full animate-spin"></div>Syncing…
              </span>
            )}
            <button
              onClick={() => syncAnalytics(true)}
              disabled={syncing || (!hasFacebook && !hasInstagram)}
              className="text-[12px] border border-gray-200 bg-white text-slate-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              <i className="fas fa-sync-alt mr-1.5"></i>Refresh
            </button>
          </div>
        </div>

        {/* ── Stat Cards ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon="fas fa-eye"         iconBg="bg-blue-600"   label="Total Reach"      value={fmt(totalReach, 'k')}     sub="Across all platforms" />
          <StatCard icon="fas fa-user-plus"   iconBg="bg-green-600"  label="New Followers"    value={fmt(totalFollowers, 'k')} sub="This campaign" />
          <StatCard icon="fas fa-paper-plane" iconBg="bg-violet-600" label="Posts Published"  value={fmt(totalPosts)}          sub={`${completionPct}% complete`} />
          <StatCard icon="fas fa-fire"        iconBg="bg-orange-500" label="Avg Eng. Rate"    value={fmt(avgEngRate, 'pct')}   sub={`${engRates.length} tracked`} />
        </div>

        {/* ── Tabs ───────────────────────────────────────────────── */}
        <div className="flex items-center gap-0.5 border-b border-gray-200 overflow-x-auto">
          {[
            ['overview',  'Overview',          'fa-chart-bar'],
            ['posts',     'Post Performance',   'fa-images'],
          ].map(([tab, label, icon]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-[12px] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className={`fas ${icon}`}></i>
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* ══ TAB: OVERVIEW ══════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

            {/* Left / main — platform + content breakdown */}
            <div className="xl:col-span-2 space-y-4">

              {/* ── Platform Performance ── */}
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                  <SectionHeader icon="fas fa-signal" title="Platform Performance" />
                </div>

                <div className="divide-y divide-gray-100">
                  {Object.entries(PLATFORM_CFG).map(([pKey, cfg]) => {
                    const isConnected = pKey === 'facebook' ? hasFacebook : pKey === 'instagram' ? hasInstagram : false;
                    const data  = platformInsights[pKey] || {};
                    const mData = manualData[pKey] || {};
                    const hasData = Object.keys(data).some(k => data[k] > 0) || Object.keys(mData).some(k => k !== 'savedAt' && mData[k]);

                    return (
                      <div key={pKey} className="p-4">
                        {/* Platform header row */}
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-lg ${cfg.bg} ${cfg.border} border flex items-center justify-center shrink-0`}>
                              <i className={`${cfg.icon} ${cfg.text} text-sm`}></i>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[13px] font-semibold text-slate-800">{cfg.label}</span>
                              {cfg.auto ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {isConnected ? 'Connected' : 'Not connected'}
                                </span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Manual entry</span>
                              )}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex items-center gap-2 shrink-0">
                            {cfg.auto && !isConnected && (
                              <button
                                onClick={connectMeta}
                                className="flex items-center gap-1.5 text-[11px] bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                              >
                                <i className="fas fa-plug text-[10px]"></i>Connect
                              </button>
                            )}
                            {cfg.auto && isConnected && (
                              <button
                                onClick={() => disconnectPlatform('facebook')}
                                disabled={disconnecting === 'facebook'}
                                className="flex items-center gap-1.5 text-[11px] border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                              >
                                {disconnecting === 'facebook'
                                  ? <div className="w-3 h-3 border border-red-400 border-t-red-600 rounded-full animate-spin"></div>
                                  : <i className="fas fa-unlink text-[10px]"></i>}
                                Disconnect
                              </button>
                            )}
                            {!cfg.auto && (
                              <button
                                onClick={() => { setManualModal(pKey); setManualForm({ ...mData }); }}
                                className="flex items-center gap-1.5 text-[11px] border border-gray-200 text-slate-600 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors"
                              >
                                <i className="fas fa-edit text-[10px]"></i>
                                {hasData ? 'Update Data' : 'Enter Data'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Connected auto platform metrics */}
                        {cfg.auto && isConnected && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                            {[
                              { key: 'impressions', label: 'Impressions', icon: 'fa-eye'         },
                              { key: 'reach',       label: 'Reach',       icon: 'fa-users'       },
                              { key: 'engagement',  label: 'Likes',       icon: 'fa-heart'       },
                              { key: 'posts',       label: 'Posts',       icon: 'fa-paper-plane' },
                            ].map(f => (
                              <div key={f.key} className={`${cfg.bg} ${cfg.border} border rounded-lg p-2.5 sm:p-3`}>
                                <div className="text-[10px] text-slate-500 mb-1">
                                  <i className={`fas ${f.icon} mr-1`}></i>{f.label}
                                </div>
                                <div className="text-[16px] sm:text-[18px] font-extrabold text-slate-800">{fmt(data[f.key], 'k')}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Instagram note — connected via Facebook */}
                        {pKey === 'instagram' && isConnected && (
                          <p className="text-[11px] text-slate-400 mt-2 italic">Instagram is connected via your Facebook account.</p>
                        )}

                        {/* Not connected */}
                        {cfg.auto && !isConnected && (
                          <p className="text-[12px] text-slate-400 italic">
                            {pKey === 'instagram'
                              ? 'Instagram connects automatically when you connect Facebook (requires Business/Creator account).'
                              : `Connect ${cfg.label} to automatically pull post metrics.`}
                          </p>
                        )}

                        {/* Manual platform — has data */}
                        {!cfg.auto && hasData && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                            {(MANUAL_FIELDS[pKey] || []).map(f => (
                              <div key={f.key} className={`${cfg.bg} ${cfg.border} border rounded-lg p-2.5 sm:p-3`}>
                                <div className="text-[10px] text-slate-500 mb-1">
                                  <i className={`fas ${f.icon} mr-1`}></i>{f.label}
                                </div>
                                <div className="text-[16px] sm:text-[18px] font-extrabold text-slate-800">{fmt(mData[f.key], 'k')}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Manual platform — no data */}
                        {!cfg.auto && !hasData && (
                          <p className="text-[12px] text-slate-400 italic">
                            No data entered yet. Click "Enter Data" to add your {cfg.label} metrics manually.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Right — AI Insights (sticky on xl) */}
            <div className="space-y-4">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-4 xl:sticky xl:top-4">
                <SectionHeader icon="fas fa-brain" title="AI Campaign Analysis" />

                {!aiInsights && !generating && (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-brain text-slate-400 text-xl"></i>
                    </div>
                    <p className="text-[12px] text-slate-500 mb-4 leading-relaxed">
                      Generate AI analysis of your campaign to discover what worked, what didn't, and get tailored recommendations.
                    </p>
                    <button
                      onClick={generateInsights}
                      className="w-full bg-slate-800 hover:bg-slate-900 text-white text-[12px] font-semibold py-2.5 px-4 rounded-lg transition-colors"
                    >
                      <i className="fas fa-magic mr-2"></i>Analyse Campaign
                    </button>
                  </div>
                )}

                {generating && (
                  <div className="text-center py-10">
                    <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-[12px] text-slate-500">Analysing your campaign data…</p>
                    <p className="text-[11px] text-slate-400 mt-1">This may take 10–20 seconds</p>
                  </div>
                )}

                {aiInsights && !generating && (
                  <div>
                    {/* Summary */}
                    {aiInsights.summary && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                        <p className="text-[12px] text-slate-700 leading-relaxed">{aiInsights.summary}</p>
                      </div>
                    )}

                    {/* What worked */}
                    {aiInsights.whatWorked?.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] font-bold text-green-700 uppercase tracking-wider mb-2">What Worked</div>
                        {aiInsights.whatWorked.map((item, i) => (
                          <InsightItem key={i} type="good" text={item} />
                        ))}
                      </div>
                    )}

                    {/* What didn't */}
                    {aiInsights.whatDidnt?.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] font-bold text-red-600 uppercase tracking-wider mb-2">What Didn't Work</div>
                        {aiInsights.whatDidnt.map((item, i) => (
                          <InsightItem key={i} type="bad" text={item} />
                        ))}
                      </div>
                    )}

                    {/* Recommendations */}
                    {aiInsights.recommendations?.length > 0 && (
                      <div className="mb-4">
                        <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider mb-2">Recommendations</div>
                        {aiInsights.recommendations.map((item, i) => (
                          <InsightItem key={i} type="tip" text={item} />
                        ))}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={generateInsights}
                        disabled={generating}
                        className="flex-1 text-[11px] border border-gray-200 text-slate-600 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <i className="fas fa-redo mr-1"></i>Regenerate
                      </button>
                      {keyChanges.length > 0 && !aiInsights.applied && (
                        <button
                          onClick={() => setApplyModal(true)}
                          className="flex-1 text-[11px] bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                          <i className="fas fa-arrow-up mr-1"></i>Apply to Strategy
                        </button>
                      )}
                      {aiInsights.applied && (
                        <span className="flex-1 text-[11px] text-green-600 text-center py-2 flex items-center justify-center gap-1">
                          <i className="fas fa-check-circle"></i>Strategy Updated
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ TAB: POST PERFORMANCE ══════════════════════════════════ */}
        {activeTab === 'posts' && (
          <div className="space-y-4">

            {/* Sync status bar */}
            {syncMeta && (
              <div className="flex items-center gap-2 text-[11px] text-slate-400 px-1">
                <i className="fas fa-check-circle text-green-500"></i>
                <span>
                  {syncMeta.synced > 0
                    ? `${syncMeta.synced} post${syncMeta.synced !== 1 ? 's' : ''} refreshed from Meta`
                    : 'All posts served from cache'}
                  {syncMeta.cached > 0 ? ` · ${syncMeta.cached} cached` : ''}
                  {' · '}{timeAgo(syncMeta.at)}
                </span>
              </div>
            )}

            {postAnalytics.length === 0 ? (
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="fas fa-paper-plane text-slate-400 text-xl"></i>
                </div>
                <h3 className="text-[13px] font-bold text-slate-700 mb-1">No Published Posts Yet</h3>
                <p className="text-[12px] text-slate-500 mb-4">
                  {hasFacebook || hasInstagram
                    ? 'Publish posts from your Campaign Calendar, then come back here to see engagement (likes, reach, impressions).'
                    : 'Connect Facebook or Instagram first, then publish posts from your Campaign Calendar to track engagement.'}
                </p>
                {hasFacebook || hasInstagram
                  ? <button onClick={() => syncAnalytics(true)} disabled={syncing} className="bg-blue-600 text-white text-[12px] px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                      <i className="fas fa-sync-alt mr-2"></i>Refresh Now
                    </button>
                  : <button onClick={connectMeta} className="bg-blue-600 text-white text-[12px] px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                      <i className="fab fa-facebook-f mr-2"></i>Connect Facebook
                    </button>
                }
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Published',    value: postAnalytics.length,                                                                           icon: 'fa-paper-plane', bg: 'bg-slate-700'   },
                    { label: 'Total Likes',  value: postAnalytics.reduce((s, p) => s + (p.engagement?.likes ?? p.engagement?.post_reactions_like_total ?? 0), 0), icon: 'fa-heart',       bg: 'bg-rose-500'    },
                    { label: 'Total Reach',  value: postAnalytics.reduce((s, p) => s + (p.engagement?.reach ?? p.engagement?.post_reach ?? 0), 0),  icon: 'fa-users',       bg: 'bg-green-600'   },
                  ].map(s => (
                    <div key={s.label} className="bg-white border-2 border-gray-200 rounded-xl p-3 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                        <i className={`fas ${s.icon} text-white text-xs`}></i>
                      </div>
                      <div>
                        <div className="text-[18px] font-extrabold text-slate-800">{fmt(s.value, 'k')}</div>
                        <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Card grid — one card per published post */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {postAnalytics.map((p, i) => (
                    <PublishedPostCard key={p.platformPostId || i} post={p} onRemove={() => removePost(p.day)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {/* ── Strategy Update Banner ─────────────────────────────── */}
        {aiInsights && keyChanges.length > 0 && !aiInsights.applied && (
          <div className="bg-white border-2 border-blue-200 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                <i className="fas fa-arrow-up text-blue-600"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-[13px] font-bold text-slate-800 mb-1">Strategy Updates Ready</h3>
                <p className="text-[12px] text-slate-500 mb-3">
                  AI has identified {keyChanges.length} recommended updates for your next 30-day strategy.
                </p>
                <div className="space-y-1.5 mb-4">
                  {keyChanges.slice(0, 4).map((u, i) => (
                    <InsightItem key={i} type="update" text={u} />
                  ))}
                  {keyChanges.length > 4 && (
                    <p className="text-[11px] text-slate-400 ml-7">+{keyChanges.length - 4} more updates…</p>
                  )}
                </div>
                <button
                  onClick={() => setApplyModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-semibold px-5 py-2 rounded-lg transition-colors"
                >
                  <i className="fas fa-arrow-up mr-2"></i>Apply Updates to Next Strategy
                </button>
              </div>
            </div>
          </div>
        )}

        {aiInsights?.applied && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <i className="fas fa-check-circle text-green-600 text-lg"></i>
            <div>
              <div className="text-[13px] font-bold text-green-800">Strategy Updated</div>
              <div className="text-[12px] text-green-700">AI recommendations have been applied to your next 30-day strategy profile.</div>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* ── Manual Data Modal ─────────────────────────────────────── */}
      {manualModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CFG[manualModal]?.bg} ${PLATFORM_CFG[manualModal]?.border} border flex items-center justify-center`}>
                  <i className={`${PLATFORM_CFG[manualModal]?.icon} ${PLATFORM_CFG[manualModal]?.text} text-sm`}></i>
                </div>
                <h2 className="text-[14px] font-bold text-slate-800">Enter {PLATFORM_CFG[manualModal]?.label} Data</h2>
              </div>
              <button onClick={() => setManualModal(null)} className="text-slate-400 hover:text-slate-600 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-[12px] text-slate-500">Enter your 30-day campaign totals from {PLATFORM_CFG[manualModal]?.label} Analytics.</p>
              <div className="grid grid-cols-2 gap-3">
                {(MANUAL_FIELDS[manualModal] || []).map(f => (
                  <div key={f.key}>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      <i className={`fas ${f.icon} mr-1 text-slate-400`}></i>{f.label}
                    </label>
                    <input
                      type="number" min="0"
                      value={manualForm[f.key] || ''}
                      onChange={e => setManualForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setManualModal(null)}
                className="flex-1 border border-gray-200 text-slate-600 text-[12px] font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={saveManual}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-semibold py-2.5 rounded-lg transition-colors">
                Save Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apply Strategy Modal ──────────────────────────────────── */}
      {applyModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-slate-800">Apply Strategy Updates?</h2>
            </div>
            <div className="p-5">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <i className="fas fa-exclamation-triangle text-amber-600 text-sm mt-0.5 shrink-0"></i>
                  <p className="text-[12px] text-amber-800 leading-relaxed">
                    This will update your Strategy Profile with the AI recommendations below. Your current roadmap will not change — only the strategy used for future generation.
                  </p>
                </div>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {keyChanges.map((u, i) => (
                  <div key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
                    <i className="fas fa-arrow-right text-blue-500 mt-0.5 text-xs shrink-0"></i>
                    <span>{insightText(u)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setApplyModal(false)}
                className="flex-1 border border-gray-200 text-slate-600 text-[12px] font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={applyStrategy} disabled={applying}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-[12px] font-semibold py-2.5 rounded-lg transition-colors">
                {applying
                  ? <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin inline-block mr-2"></div>Applying…</>
                  : 'Confirm & Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
